import { useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Check, Settings2 } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { HasAccess } from "@/components/common/has-access";
import { cn } from "@/lib/utils";
import useActiveRegions from "../hooks/use-active-regions";
import { ALL_REGIONS_CODE, regionPath, regionlessPath } from "../constants";
import { codeToFlag } from "../utils/code-to-flag";

// Global region selector for the sidebar — a searchable dropdown with three
// rows: (1) search, (2) region list, (3) an admin-only "Advanced settings"
// link to the Regions page. The region lives in the URL (/:region/...), so
// selecting one navigates to the same page under the new region segment;
// RegionLayout persists it to localStorage.
export default function RegionToggle() {
  const { region: activeRegion = ALL_REGIONS_CODE } = useParams();
  const { data: regions, isLoading } = useActiveRegions();
  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const list = useMemo(() => regions ?? [], [regions]);

  const query = search.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!query) return list;
    return list.filter(
      (r) =>
        r.display_name.toLowerCase().includes(query) ||
        r.code.toLowerCase().includes(query),
    );
  }, [list, query]);

  // The "All regions" meta-option (no filter). Surfaces unless the search
  // clearly excludes it.
  const showAll = !query || "all regions".includes(query);

  const handleSelect = (code: string) => {
    // Swap the region segment, keep the rest of the path + query.
    navigate(
      regionPath(code, regionlessPath(location.pathname)) + location.search,
    );
    setOpen(false);
    setSearch("");
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          className="h-6 w-[68px] gap-1.5 rounded-full border border-white/30 bg-white/10 pl-2 pr-1 text-white hover:bg-white/20 hover:text-white"
        >
          <span>{codeToFlag(activeRegion)}</span>
          <span className="uppercase">{activeRegion}</span>
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-56 p-0" align="start">
        <div className="flex flex-col">
          {/* Row 1 — search */}
          <div className="border-b p-2">
            <Input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search regions…"
              className="h-8 border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </div>

          {/* Row 2 — list */}
          <div className="max-h-[220px] overflow-auto p-1">
            {isLoading ? (
              <div className="p-2 text-sm text-muted-foreground">Loading…</div>
            ) : !showAll && filtered.length === 0 ? (
              <div className="p-2 text-sm text-muted-foreground">
                No regions found
              </div>
            ) : (
              <div className="flex flex-col gap-0.5">
                {showAll && (
                  <Button
                    type="button"
                    variant="ghost"
                    className={cn(
                      "h-auto justify-start gap-2 py-1.5",
                      activeRegion === ALL_REGIONS_CODE && "bg-muted",
                    )}
                    onClick={() => handleSelect(ALL_REGIONS_CODE)}
                  >
                    <span>{codeToFlag(ALL_REGIONS_CODE)}</span>
                    <span className="flex-1 text-left">All Regions</span>
                    {activeRegion === ALL_REGIONS_CODE && (
                      <Check className="h-4 w-4 shrink-0" />
                    )}
                  </Button>
                )}
                {filtered.map((region) => {
                  const isActive = region.code === activeRegion;
                  return (
                    <Button
                      key={region.id}
                      type="button"
                      variant="ghost"
                      className={cn(
                        "h-auto justify-start gap-2 py-1.5",
                        isActive && "bg-muted",
                      )}
                      onClick={() => handleSelect(region.code)}
                    >
                      <span>{codeToFlag(region.code)}</span>
                      <span className="flex-1 text-left">
                        {region.display_name}
                      </span>
                      {isActive && <Check className="h-4 w-4 shrink-0" />}
                    </Button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Row 3 — advanced settings (super_admin + admin only) */}
          <HasAccess permission="invite">
            <div className="border-t p-1">
              <Button
                type="button"
                variant="ghost"
                className="h-auto w-full justify-start gap-2 py-1.5 text-muted-foreground"
                onClick={() => {
                  setOpen(false);
                  navigate(regionPath(activeRegion, "/regions"));
                }}
              >
                <Settings2 className="h-4 w-4" />
                Advanced settings
              </Button>
            </div>
          </HasAccess>
        </div>
      </PopoverContent>
    </Popover>
  );
}
